import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, DashboardLayout } from './components/Layout';
import { 
  Home, 
  Create, 
  Customize, 
  Download,
  Login,
  Signup,
  ForgotPassword,
  Dashboard,
  Mockups,
  Library,
  Assets,
  MockupToPrint,
  Brand,
  BrandTeam,
  BrandCraft,
  SceneScaper,
  VideoMockups,
  Support,
  Settings,
  Special,
  SpecialLight,
  Offer,
  UpgradeProfessional,
  UpgradeAgency,
  Pricing,
  Contact,
  PrivacyPolicy,
  RefundPolicy,
  CookiePolicy,
  TermsOfService,
  Disclaimer,
  ResetPassword
} from './pages';
import UpgradeNicheDominator from './pages/UpgradeNicheDominator';
import Agency from './pages/Agency';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import AuthGuard from './components/AuthGuard';
import AdminGuard from './components/AdminGuard';
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserManagement from './components/Admin/UserManagement';
import UserDetail from './components/Admin/UserDetail';
import Statistics from './components/Admin/Statistics';
import AuditLog from './components/Admin/AuditLog';
import WebhookLogs from './components/Admin/WebhookLogs';
import AdminEmails from './pages/Dashboard/AdminEmails';
import ImpersonateCallback from './pages/ImpersonateCallback';
import { clearCacheOnStartup } from './utils/devCacheClear';

function AppContent() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" />} />

      {/* Marketing Pages */}
      <Route path="/special" element={<Special />} />
      <Route path="/special-light" element={<SpecialLight />} />
      <Route path="/offer" element={<Offer />} />
      <Route path="/upgrade/professional" element={<UpgradeProfessional />} />
      <Route path="/upgrade/agency" element={<UpgradeAgency />} />
      <Route path="/upgrade/nicheDominator" element={<UpgradeNicheDominator />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<Contact />} />

      {/* Policy Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<Disclaimer />} />

      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />
      <Route path="/auth/impersonate" element={<ImpersonateCallback />} />

      {/* Protected Dashboard Routes */}
      <Route 
        path="/dashboard" 
        element={
          <AuthGuard requireAuth={true}>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="create" element={<Create />} />
        <Route path="customize" element={<Customize />} />
        <Route path="download" element={<Download />} />
        <Route path="mockups" element={<Mockups />} />
        <Route path="library" element={<Library />} />
        <Route path="assets" element={<Assets />} />
        <Route path="mockup-to-print" element={<MockupToPrint />} />
        <Route path="brand" element={<Brand />} />
        <Route path="brand-team" element={<BrandTeam />} />
        <Route path="brand-craft" element={<BrandCraft />} />
        <Route path="scenescaper" element={<SceneScaper />} />
        <Route path="video-mockups" element={<VideoMockups />} />
        <Route path="agency" element={<Agency />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="statistics" element={<Statistics />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="webhooks" element={<WebhookLogs />} />
        <Route path="emails" element={<AdminEmails />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    clearCacheOnStartup();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NotificationProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </NotificationProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
